import {
  openPortalDialog,
  PortalDialogProps,
} from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { createWithdrawValidaion } from "@app/validations";
import { useState } from "react";
import clsx from "clsx";
import useObservable from "@core/hooks/use-observable.hook";
import StorageService from "@core/services/storage";
import { RefCode, Withdraw } from "@app/types";
import { MAX_LEN } from "@app/constants";
import WithdrawService from "@app/services/http/withdraw.service";
import { formatMoneyVN } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import ConfirmModal from "../confirm-modal";
import { IcBanknote, IcX } from "../icons";
import { btn, btnLg, btnPrimary, iconBtn, input } from "../ui";

/** Yêu cầu rút tiền hoa hồng. Logic giữ nguyên như bản cũ (Formik + createWithdrawValidaion + xác nhận + nhớ tài khoản). */
function WithdrawModel({ portalDialogRef, portalData }: PortalDialogProps) {
  const t = useT();
  const refCode: RefCode = portalData.refCode;
  const [isSaveBank, setIsSaveBank] = useState(true);

  const { subscribeOnce } = useObservable();

  const accountBank = StorageService.getObject("withdraw_infor") as Withdraw;

  const initialValues: Withdraw = {
    money: 0,
    bank_name: accountBank?.bank_name || "",
    bank_number: accountBank?.bank_number || "",
    bank_owner: accountBank?.bank_owner || "",
  };

  const handleSubmit = (values: Withdraw) => {
    const obsConfirm = openPortalDialog(ConfirmModal, {
      message:
        "Bạn chắc chắn với thông tin tài khoản nhận tiền, chúng tôi không chịu trách nhiệm nếu bạn nhập sai thông tin",
    });

    obsConfirm.afterClosed().subscribe((res) => {
      if (res?.isAccept) {
        const withdraw: Withdraw = {
          money: values.money,
          bank_name: values.bank_name,
          bank_number: values.bank_number,
          bank_owner: values.bank_owner,
        };

        if (isSaveBank) {
          StorageService.setObject("withdraw_infor", withdraw);
        }

        if (withdraw) {
          subscribeOnce(WithdrawService.createWithdrawRequest(withdraw), () => {
            portalDialogRef.close({ ok: 1 });
          });
        }
      }
    });
  };

  const fields: [keyof Withdraw, string, string][] = [
    ["bank_name", "Tên ngân hàng", "Ví dụ: Vietcombank"],
    ["bank_number", "Số tài khoản", "Nhập số tài khoản"],
    ["bank_owner", "Chủ tài khoản", "Viết hoa không dấu, ví dụ NGUYEN VAN A"],
  ];

  return (
    <Modal onCancel={() => portalDialogRef.close()} buttonCancelInChildren={false}>
      <div className="flex w-[min(460px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start justify-between gap-3 border-b border-line px-6 pb-4 pt-5">
          <div>
            <h2 className="text-[19px] font-bold">{t("Yêu cầu rút tiền")}</h2>
            <p className="m-0 mt-0.5 text-[13px] text-muted">
              {t("Số dư hoa hồng")}: <b className="tabular-nums text-fg">{formatMoneyVN(refCode?.commission_balance || 0)}đ</b>
            </p>
          </div>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8")} aria-label={t("Đóng")} onClick={() => portalDialogRef.close()}>
            <IcX className="h-4 w-4" />
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={createWithdrawValidaion}
          onSubmit={handleSubmit}
          validateOnChange
          validateOnBlur
        >
          {({ setFieldValue, errors, touched }) => (
            <Form className="flex flex-col gap-3.5 px-6 py-5">
              <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
                {t("Số tiền rút")}
                <div className="flex gap-2">
                  <Field
                    name="money"
                    type="number"
                    min={0}
                    className={clsx(input, "!h-10 tabular-nums", touched.money && errors.money && "!border-bad")}
                  />
                  <button
                    type="button"
                    className="h-10 flex-none rounded-[10px] border border-line px-3 text-[12.5px] font-semibold text-brand hover:bg-brand-soft"
                    onClick={() => setFieldValue("money", refCode?.commission_balance || 0)}
                  >
                    {t("Rút hết")}
                  </button>
                </div>
                <ErrorMessage name="money" component="small" className="font-medium text-bad" />
                <small className="font-normal text-faint">
                  {t("Tối thiểu")} {formatMoneyVN(500000)}đ
                </small>
              </label>

              {fields.map(([name, label, placeholder]) => (
                <label key={name} className="flex flex-col gap-1 text-[12.5px] font-semibold text-muted">
                  {t(label)}
                  <Field
                    name={name}
                    maxLength={MAX_LEN.BANK}
                    placeholder={t(placeholder)}
                    className={clsx(input, "!h-10", touched[name] && errors[name] && "!border-bad")}
                  />
                  <ErrorMessage name={name} component="small" className="font-medium text-bad" />
                </label>
              ))}

              <label className="flex cursor-pointer items-center gap-2 text-[13px] text-muted">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--brand)]"
                  checked={isSaveBank}
                  onChange={() => setIsSaveBank(!isSaveBank)}
                />
                {t("Nhớ tài khoản ngân hàng cho lần sau")}
              </label>

              <button type="submit" className={clsx(btn, btnPrimary, btnLg, "mt-1 w-full")}>
                <IcBanknote className="h-[18px] w-[18px]" />
                {t("Gửi yêu cầu rút tiền")}
              </button>
              <p className="m-0 text-center text-xs text-faint">
                {t("Số tiền được trừ khỏi số dư hoa hồng ngay khi gửi và chuyển về ngân hàng sau khi PCRender duyệt.")}
              </p>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}

export default WithdrawModel;
